import has from 'lodash/has';
import { SlideFile } from '@tencent/slide-sdk-model';
import { reaction } from 'mobx';
import { ChunkDataManager } from '../../../core/managers/chunk-data-manager';
import { SlideInitialDataType } from '../../../core/interfaces/slide-initial-data-service.interface';
import type { ISlideInitialData } from '../../../core/interfaces/slide-initial-data-service.interface';
import type { IChunksGenerator } from '../../../core/interfaces/chunks-generator.interface';
import type { IClientVarsService } from '../../../core/interfaces/client-vars-service.interface';
import type { ICommandData, ISlideFileData } from '@tencent/slide-sdk-model';
import type { OnlineLoadConvertHandlers } from '../core/interfaces/online-edit-data-fetcher.interface';
import { IOnlineApiService, IChunkData } from '../core/interfaces/online-api-service.interface';

export class DocxOnlineEditDataFetcher {
    private fetchMaxSlideCount = 10;
    private startIndex = 0;
    private fetchQueue: Array<(successCb: () => void, errCb: () => void) => void> = [];
    private fetchingQueue: Array<(successCb: () => void, errCb: () => void) => void>  = [];
    private fetchTimer;

    public constructor(
        private clientVarsService: IClientVarsService,
        private onlineApiService: IOnlineApiService,
    ) {
        this.startIndex = (this.firstChunkInfo?.startIndex ?? 0) + (this.firstChunkInfo?.count ?? 0);
    }

    public async loadPresentation(handlers: OnlineLoadConvertHandlers = {}): Promise<ISlideInitialData> {
        const chunkDataManager = new ChunkDataManager<{ commands: ICommandData[] }>();
        const chunksGenerator = chunkDataManager.toChunksGenerator().advance();

        return new Promise((resolve, reject) => {
            const dispose = (err?: Error) => {
                chunkDataManager.dispose(err);
                if (err) {
                    handlers.onFatalError?.(err);
                    reject(err);
                }
            };
            handlers.onHead?.(this.totalChunkCount);

            resolve({
                type: SlideInitialDataType.CommandChunkData,
                data: {
                    head: this.getFirstChunkData(),
                    chunks: chunksGenerator.init(this.totalChunkCount, this.firstChunkInfo?.count),
                },
            });

            setTimeout(() => {
                reaction(() => chunksGenerator.done, (done) => {
                    if (done) {
                        handlers.onEnd?.(chunksGenerator.total);
                        dispose();
                    }
                }, {
                    fireImmediately: true,
                });
                // this.startFetch(chunkDataManager, chunksGenerator, handlers).catch(reject);
                this.startFetchConcurrency(chunkDataManager, chunksGenerator, handlers);
            });
        });
    }

    private startFetchConcurrencyTimer() {
        const maxConcurrencyCount = 5;
        this.fetchTimer = setInterval(() => {
            if (!this.fetchQueue.length && !this.fetchingQueue.length) {
                clearInterval(this.fetchTimer);
                return;
            }
            const fetchArr = this.fetchQueue.splice(0, maxConcurrencyCount - this.fetchingQueue.length);
            if (fetchArr.length) {
                this.fetchingQueue.push(...fetchArr);
                fetchArr.forEach(fetch => fetch(
                    () => this.fetchingQueue.splice(this.fetchingQueue.indexOf(fetch), 1),
                    () => clearInterval(this.fetchTimer),
                ));
            }
        }, 16);
    }

    private getSlideFileJSON(data: string): ICommandData {
        return new SlideFile({ presentation: data }).getCommandData();
    }


    private getFirstChunkData() {
        const clientVarsData = this.clientVarsService.getClientVars();
        const initialAttributedText = clientVarsData.collab_client_vars?.initialAttributedText;
        const initialData = initialAttributedText?.text ?? '{}';
        const { isPaginated } = initialAttributedText?.slide ?? { isPaginated: true };
        const slideFileData: ISlideFileData = isPaginated ? { presentation: initialData } : JSON.parse(initialData);
        return new SlideFile(slideFileData).getCommandData();
    }

    /**
     * 算上首页单独的分片，总共需要拉取的分片数
     * @returns
     */
    private get totalChunkCount(): number {
        if (!this.firstChunkInfo?.total || this.firstChunkInfo?.total === 1) return 1;
        return 1 + Math.ceil((this.firstChunkInfo.total - this.firstChunkInfo.count) / this.fetchMaxSlideCount);
    }

    // private async startFetch(
    //     chunkDataManager: ChunkDataManager<{ commands: ICommandData[]; }>,
    //     chunksGenerator: IChunksGenerator<{ commands: ICommandData[]; }>,
    //     handlers: OnlineLoadConvertHandlers,
    // ) {
    //     let chunkIndex = 1;
    //     while (chunkIndex < this.totalChunkCount) {
    //         try {
    //             const  { data, isLast } =  await this.fetchChunkData(this.startIndex);
    //             chunksGenerator.updateTotal(this.totalChunkCount);
    //             chunkDataManager.add(chunkIndex, { commands: [this.getSlideFileJSON(data)] });
    //             handlers.onChunk?.(chunkIndex, this.totalChunkCount);
    //             chunkIndex += 1;
    //             if (isLast) {
    //                 break;
    //             }
    //         } catch (e) {
    //             handlers.onFatalError?.(e);
    //             break;
    //         }
    //     }
    // }

    private startFetchConcurrency(
        chunkDataManager: ChunkDataManager<{ commands: ICommandData[]; }>,
        chunksGenerator: IChunksGenerator<{ commands: ICommandData[]; }>,
        handlers: OnlineLoadConvertHandlers,
    ) {
        this.startFetchConcurrencyTimer();
        let chunkIndex = 1;
        while (chunkIndex < this.totalChunkCount) {
            const startIndex = this.startIndex + ((chunkIndex - 1) * this.fetchMaxSlideCount);
            this.fetchQueue.push(((chunkIndex, startIndex) => (successCb: () => void, errCb: () => void) => {
                const fetchPromise = this.fetchChunkData(startIndex);
                fetchPromise.then(({ data }) => {
                    chunksGenerator.updateTotal(this.totalChunkCount);
                    chunkDataManager.add(chunkIndex, { commands: [this.getSlideFileJSON(data)] });
                    handlers.onChunk?.(chunkIndex, this.totalChunkCount);
                    successCb();
                }).catch((e) => {
                    errCb();
                    handlers.onFatalError?.(e);
                });
            })(chunkIndex, startIndex));
            chunkIndex += 1;
        }
    }

    private get firstChunkInfo() {
        return this.clientVarsService.getClientVars()?.collab_client_vars?.initialAttributedText?.slide;
    }

    private get globalPadId() {
        return this.clientVarsService.getClientVars().collab_client_vars?.globalPadId ?? '';
    }

    private get requestRevision(): number {
        return this.clientVarsService.getClientVars().collab_client_vars?.rev || 0;
    }

    private async fetchChunkData(
        startIndex = 0,
        retryCount = 0,
    ) {
        const pendingPromise = this.createPendingPromise();
        this.fetchChunkDataByPromise(startIndex, retryCount, pendingPromise);
        return pendingPromise.promise;
    }

    /**
     * 通过promise的方式拉取分片数据，涉及重试机制
     * @param startIndex
     * @param retryCount
     * @param pendingPromise
     * @returns
     */
    private async fetchChunkDataByPromise(
        startIndex = 0,
        retryCount = 0,
        pendingPromise: ReturnType<typeof this.createPendingPromise>,
    ) {
        try {
            const res = await this.onlineApiService.getChunkData({
                padId: this.globalPadId,
                startIndex,
                count: this.fetchMaxSlideCount,
                revisionVersion: this.requestRevision,
            });
            if (!this.checkFetchChunkDataResult(res)) {
                throw Error(JSON.stringify(res.data ?? res));
            }
            const data = res.data.data as IChunkData;
            // 服务端返回的count只会可能请求的count小，手动修正
            this.fetchMaxSlideCount = data.count;
            this.startIndex = data.count + data.start_index;
            pendingPromise.resolve({
                data: data.data,
                isLast: data.total <= this.startIndex,
            });
        } catch (err) {
            if (retryCount < 2) {
                await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
                this.fetchChunkDataByPromise(startIndex, retryCount + 1, pendingPromise);
                return;
            }
            pendingPromise.reject(err.toString());
        }
    }

    private checkFetchChunkDataResult(res: any): boolean {
        const data: IChunkData = res?.data?.data;
        if (has(data, 'count')
            && has(data, 'data')
            && has(data, 'start_index')
            && has(data, 'total')
            && has(data, 'revision_version')
        ) {
            return true;
        }
        return false;
    }

    /**
     * 创建一个pending状态的promise
     * @returns
     */
    private createPendingPromise() {
        let resolve: (res: { data: string; isLast: boolean }) => void = () => [];
        let reject: (e: any) => void = e => e;
        const promise = new Promise<{ data: string; isLast: boolean }>((res, rej) => {
            resolve = res;
            reject = rej;
        });
        return { promise, resolve, reject };
    }
}
